// --------------------------
// IMPORTS
// --------------------------
import { useEffect, useState } from "react";
import {
  Form,
  Input,
  message,
  Select,
  Checkbox,
  InputNumber,
  Modal,
} from "antd";

import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import TableContent from "../../components/atoms/TableContent";
import TopContainerHeader from "../../components/molecules/TopComponentHeader";
import useCurrentPath, {
  deFaultHandlePaginationChange,
  formateDateTime,
  handleDelete,
  sortData,
} from "../../utils/Constants";
import SubmitButton from "../../components/atoms/Buttons/SubmitButton";
import {
  addUserThunk,
  deleteUserThunk,
  getUserListThunk,
  getRoleListThunk,
  editUserThunk,
} from "../../store/thunks/usersThunk";
import EditForm from "../../components/molecules/Forms/editForm";
import media from "../../media";
import { selectedRowInfo } from "../../store/slices/authSlice";
import { getDepartmentListThunk } from "../../store/thunks/masterDataThunk";

// --------------------------
// REUSABLE CELL COMPONENT
// --------------------------
const EditableCell = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  roles,
  departments,
  ...restProps
}) => {
  let inputNode;
  if (dataIndex === "role") {
    inputNode = (
      <Select
        isclearable="true"
        options={roles}
        filterOption={(input, option) =>
          option.label.toLowerCase().includes(input.toLowerCase())
        }
      />
    );
  } else if (dataIndex === "department") {
    inputNode = (
      <Select
        isclearable="true"
        options={departments}
        filterOption={(input, option) =>
          option.label.toLowerCase().includes(input.toLowerCase())
        }
      />
    );
  } else {
    inputNode = inputType === "number" ? <InputNumber /> : <Input />;
  }
  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{
            margin: 0,
          }}
          rules={[
            {
              required: true,
              message: `Please Input ${title}!`,
            },
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

// --------------------------
// MAIN COMPONENT
// --------------------------
const Users = () => {
  //Dispatch + Custom Hooks
  const dispatch = useDispatch();
  const location = useLocation();
  const currentPath = useCurrentPath();
  const [form] = Form.useForm();
  const [tableForm] = Form.useForm();

  // --------------------------
  // REDUX STATE
  // --------------------------
  const storeRole = useSelector((state) => state.auth.role);
  const storeToken = useSelector((state) => state.auth.accessToken);
  const storeLIUId = useSelector((state) => state.auth.lIUId);
  const users = useSelector((state) => state.users.list) || [];
  const usersLoading = useSelector((state) => state.users.loading);
  const storePageInfo = useSelector((state) => state.users.pageInfo);
  const storeAccessTokenExpiresOn = useSelector(
    (state) => state.auth.accessTokenExpiresOn
  );

  // --------------------------
  // LOCAL STATES
  // --------------------------
  const [showLoader, setShowLoader] = useState(true);
  const [storeTotalCount, setStoreTotalCount] = useState(0);  
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [data, setData] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [canSendExpenseRequest, setCanSendExpenseRequest] = useState();
  const [canDelegateAccess, setCanDelegateAccess] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]),
    [roles, setRoles] = useState([]),
    [managers, setManagers] = useState([]),
    [departments, setDepartments] = useState([]),
    [selectedRole, setSelectedRole] = useState();
  const [profilePicInfo, setProfilePicInfo] = useState("");
  const [profilePicWithUrlChecked, setProfilePicWithUrlChecked] =
    useState(false);
  const [searchText, setSearchText] = useState("");
  const [editingKey, setEditingKey] = useState("");
  const isEditing = (record) => record.key === editingKey;

  // --------------------------
  // API CALLS
  // --------------------------

  // Fetch users, roles, departments
  const fetchUsers = async () => {    
    const res = await dispatch(
      getUserListThunk({
        route: currentPath,
        pagination: {
          current: 1,
          pageSize: process.env.REACT_APP_DEFAULT_PAGINATION_PAGESIZE,
        },
      })
    ).unwrap();

    setStoreTotalCount(res.totalCount || 0);
    setShowLoader(false);
  };

  // --------------------------
  // USE EFFECTS
  // --------------------------
  useEffect(() => {
    setFilteredUsers(users); // sync on data change
  }, [users]);
  useEffect(() => {
    fetchUsers();

    dispatch(getRoleListThunk())
      .unwrap()
      .then((res) => {
        setRoles(res || []); // update local state
      })
      .catch((err) => console.error(err));
    dispatch(getDepartmentListThunk())
      .unwrap()
      .then((res) => {
        setDepartments(res || []); // update local state
      })
      .catch((err) => console.error(err));
  }, [currentPath, dispatch]);

  // --------------------------
  // HANDLERS
  // --------------------------
  const openEditModal = (record) => {
    setEditingUser(record); // store the record to edit
    tableForm.setFieldsValue({
      empID: record?.empID,
      name: record?.name,
      email: record?.email,
      role: record?.role,
      status: record?.status,
      canDelegateAccess: record.canDelegateAccess,
    });
    setIsEditModalOpen(true); // open the modal
  };

  const handleCheckboxChange = (e) => {
    setProfilePicWithUrlChecked(e.target.checked);
  };

  const inputComponent = <Input className="w-100 p-2 rounded-2" />;

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    // Read image parameters
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();
      img.src = e.target.result;
      img.onload = () => {
        // Check for SVG file type
        const isSVG = file.type === "image/svg+xml";
        img.src && setProfilePicInfo(img.src);
      };
    };

    reader.readAsDataURL(file);
  };

  const getBase64ImageExtension = (urlOrBase64) => {
    // Check if the input is a Base64 string
    if (urlOrBase64.startsWith("data:image")) {
      // Extract the MIME type from the Base64 string
      const mimeTypeMatch = urlOrBase64.match(
        /^data:([a-zA-Z0-9]+\/[a-zA-Z0-9-.+]+).*,.*/
      );
      if (mimeTypeMatch && mimeTypeMatch[1]) {
        // Extract the file extension from the MIME type
        const extension = mimeTypeMatch[1].split("/")[1];
        return extension;
      } else {
        // Default to empty string if no extension found
        return "";
      }
    } else {
      // Extract the file extension from the URL
      const ext = urlOrBase64.split(".").pop().split("?")[0];
      return ext;
    }
  };

  const headers = {
    "Content-Type": "multipart/form-data", // Make sure to set the content type
  };

  // --------------------------
  // TABLE COLUMNS
  // --------------------------
  const columns = [
    {
      title: "Sr. No.",
      render: (text, record, index) => index + 1,
      className: "all-nowrap-column",
    },
    {
      title: "Role",
      dataIndex: "role",
      editable: true,
      render: (text) => <>{text}</>,
    },
    {
      title: "Emp. ID",
      dataIndex: "empID",
      className: "all-nowrap-column",
      render: (text) => <>{text}</>,
    },
    {
      title: "Name",
      dataIndex: "name",
      editable: true,
      className: "all-nowrap-column",
      render: (text) => <>{text}</>,
    },
    {
      title: "Email",
      dataIndex: "email",
      editable: true,
      className: "all-nowrap-column",
      render: (text) => <>{text}</>,
    },
    {
      title: "Department",
      dataIndex: "department",
      className: "all-nowrap-column",
      render: (text) => <>{text}</>,
    },
    {
      title: "Status",
      dataIndex: "status",
      editable: true,
      className: "all-nowrap-column",
      sorter: (a, b) => a.status.localeCompare(b.status),
      render: (text) => (
        <>
          <span className={`${text.toLowerCase()}Status`}>{text}</span>
        </>
      ),
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      className: "all-nowrap-column",
      render: (text) => text && formateDateTime(text),
    },
    ...(storeRole === process.env.REACT_APP_SROLE ||
    storeRole === process.env.REACT_APP_ADROLE
      ? [
          {
            title: "Updated At",
            dataIndex: "updatedAt",
            className: "all-nowrap-column",
            render: (text) => text && formateDateTime(text),
          },
          {
            title: "Action",
            dataIndex: "action",

            render: (_, record) => (
              <span
                className="table-round-btn"
                onClick={() => openEditModal(record)}
              >
                <img src={media.editIcon.svg} alt="Edit icon" />
                <span className="text-size-12 dark-black-color">Edit</span>
              </span>
            ),
          },
        ]
      : []),
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType: "text",
        dataIndex: col.dataIndex,
        title: col.title,
        roles: roles,
        departments: departments,
        editing: isEditing(record),
      }),
    };
  });

  // rowSelection object indicates the need for row selection
  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedRowKeys(selectedRowKeys);
      const selectedRowKeysChildInfoArr = selectedRows.map((row) => row.key);

      dispatch(
        selectedRowInfo({
          selectedRowKeysChildInfo: selectedRowKeysChildInfoArr,
        })
      );
    },
    getCheckboxProps: (record) => ({
      disabled: record.name === "Disabled User",
      // Column configuration not to be checked
      name: record.name,
    }),
  };

  // --------------------------
  // FORM FIELDS
  // --------------------------
  const formFields = [
    {
      isVisible: true,
      label: "Role",
      name: "role",
      rules: [
        {
          required: true,
          message: "Please enter role",
        },
      ],
      placeholder: "Select Role",
      type: "select",
      options: roles,
      onChange: (val, data) => {
        setSelectedRole(data?.label);
      },
    },
    {
      isVisible: selectedRole === process.env.REACT_APP_AROLE ? true : false,
      label: "Can send an Expense Request?",
      name: "canSendExpenseRequest",
      rules: [
        {
          required: true,
          message: "Please select an option",
        },
      ],
      placeholder: "Select an option",
      type: "select",
      options: [
        { label: "Yes", value: true },
        { label: "No", value: false },
      ],
      onChange: (val) => {
        setCanSendExpenseRequest(val);
      },
    },
    {
      isVisible: true,
      label: "Employee ID",
      name: "empID",
      rules: [
        {
          required: true,
          message: "Please input employee id",
        },
      ],
      placeholder: "Enter the employee id",
      type: "input",
    },
    {
      isVisible: true,
      label: "Name",
      name: "name",
      rules: [
        {
          required: true,
          message: "Please input name",
        },
      ],
      placeholder: "Enter the name",
      type: "input",
    },
    {
      isVisible: true,
      label: "Email",
      name: "email",
      rules: [
        { required: true, message: "Please input your email!" },
        {
          type: "email",
          message: "Please enter a valid email address",
        },
      ],
      placeholder: "Enter the email",
      type: "input",
    },
    {
      isVisible: true,
      label: "Department",
      name: "department",
      rules: [
        {
          required: true,
          message: "Please enter department",
        },
      ],
      placeholder: "Select Department",
      type: "select",
      multiple: true,
      options: departments,
      filterOption: (input, option) =>
        option.label.toLowerCase().includes(input.toLowerCase()),
    },
    {
      isVisible: true,
      label: "Expense Category",
      name: "expenseCatID",
      rules: [""],
      placeholder: "Select an option",
      type: "select",
      options: [
        { label: "OPEX", value: "OPEX" },
        { label: "CAPEX", value: "CAPEX" },
      ],
    },
    {
      isVisible: true,
      label: "Allow this user to delegate access",
      name: "canDelegateAccess",
      rules: [
        {
          required: true,
          message: "Please select an option",
        },
      ],
      placeholder: "Select an option",
      type: "select",
      options: [
        { label: "Yes", value: true },
        { label: "No", value: false },
      ],
      onChange: (val) => {
        setCanDelegateAccess(val);
      },
    },
    {
      isVisible: true,
      type: "custom",
      render: () => (
        <>
          <Form.Item label="Profile Image" name="profilePicUrl">
            {profilePicWithUrlChecked ? (
              inputComponent
            ) : (
              <input
                type="file"
                onChange={handleFileChange}
                className="upload-list-block"
              />
            )}
          </Form.Item>
          <Form.Item label="" name="profilePicUrl">
            <Checkbox
              checked={profilePicWithUrlChecked}
              onChange={handleCheckboxChange}
              className="form-label-antd profilePicWithUrlCheckBox"
              name="profilePicWithUrlCheckBox"
            >
              Upload with url
            </Checkbox>
          </Form.Item>
        </>
      ),
    },
  ];

  // --------------------------
  // JSX RETURN
  // --------------------------
  return (
    <div className="container">
      {selectedRole}
      <div className="bgi-no-repeat card-stretch rounded-lg p-0">
        <TopContainerHeader
          sectionNameClass="col-lg-7 text-left commonTitleText"
          sectionTitle="Users"
          sectionDescription={
            showLoader ? "Loading..." : `${storeTotalCount} Found`
          }
          hasSectionButton={false}
          hasSearch={true}
          hasSearchButton="general"
          addNewButtonClassName="btn mx-auto px-5 py-2 defaultbutton rounded-lg"
          name="Add New User"
          searchClassName="table-search"
          searchPlaceholder="Search User's Role, Emp. ID, Name, Email, Department and Status"
          onButtonClick={() => setIsAddModalOpen(true)}
          searchData={users}
          onFilteredData={setFilteredUsers}
          searchKeys={[
            "role",
            "empID",
            "name",
            "email",
            "department",
            "status",
          ]}
          addFormModalOpen={isAddModalOpen}
          addFormSetIsModalOpen={setIsAddModalOpen}
          addFormModalShowLoader={setShowLoader}
          payload={(values) => ({
            route: currentPath,
            roleID: values?.role,
            empID: parseInt(values?.empID),
            name: values?.name,
            email: values?.email,
            departmentID: values?.department,
            imageName:
              parseInt(values?.empID) +
              "_" +
              values?.name?.replaceAll(" ", "_"),
            uploadType:
              profilePicWithUrlChecked === true ? "thumbUrl" : "other",
            profilePicUrl:
              profilePicWithUrlChecked === true
                ? values?.profilePicUrl
                : profilePicInfo
                ? profilePicInfo
                : null,
            ext:
              profilePicWithUrlChecked === true
                ? getBase64ImageExtension(values?.profilePicUrl)
                : profilePicInfo
                ? getBase64ImageExtension(profilePicInfo)
                : "", //values?.profilePicUrl?.split(".").pop()
            //createdAt: new Date().toISOString(),
            canSendExpenseRequest: canSendExpenseRequest,
            canDelegateAccess: canDelegateAccess,
            expenseCatID: values?.expenseCatID,
            historySource: "backend",
            historyAction: "create",
            historyUserID: storeLIUId,
          })}
          list={addUserThunk}
          formFields={formFields}
          headers={headers}
          successMessage="User added successfully."
          onSuccess={fetchUsers}
          selectedRowKeys={
            storeRole === process.env.REACT_APP_SROLE && selectedRowKeys
          }
          deleteOnClick={() => {
            handleDelete({
              selectedRowKeys,
              dispatch,
              deleteThunk: deleteUserThunk,
              route: "users",
              actionName: "user",
              onSuccess: () => setSelectedRowKeys([]),
            });
          }}
          searchText={searchText}
          setSearchText={(value) => {
            setShowLoader(true);
            setSearchText(value);
            setTimeout(() => setShowLoader(false), 500);
          }}
        />

        <div className="card p-5 rounded-lg paddingTop-0">
          <div className="table-area">
            <div className="table-responsive">
              <div className="tab-content" id="alltable-size">
                <Form form={tableForm} component={false}>
                  <TableContent                    
                    bordered={false}
                    columns={mergedColumns}
                    rowClassName="editable-row"
                    rowSelection={rowSelection}
                    storeList={filteredUsers}
                    components={{
                      body: {
                        cell: EditableCell,
                      },
                    }}
                    pagination={storePageInfo}
                    loading={showLoader}
                    totalCount={storeTotalCount}
                    handlePaginationChange={(page, pageSize) =>
                      deFaultHandlePaginationChange(
                        (type, pagination) =>
                          dispatch(
                            getUserListThunk({ route: currentPath, pagination })
                          ),
                        page,
                        pageSize,
                        storeTotalCount
                      )
                    }
                  />
                </Form>
              </div>
            </div>
          </div>
        </div>
        <EditForm
          title="Edit User"
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          form={tableForm}
          fields={[
            {
              label: "Employee ID",
              name: "empID",
              type: "input",
              props: { disabled: true },
            },
            {
              label: "Role",
              name: "role",
              type: "select",
              options: roles, // pass your roles array [{ label, value }]
              rules: [{ required: true, message: "Please select user role!" }],
            },
            {
              label: "Name",
              name: "name",
              type: "input",
              rules: [{ required: true, message: "Please input user name!" }],
            },
            {
              label: "Email",
              name: "email",
              type: "input",
              props: { type: "email" },
              rules: [{ required: true, message: "Please input user email!" }],
            },
            {
              label: "Allow this user to delegate access",
              name: "canDelegateAccess",
              type: "select",
              options: [
                { label: "Yes", value: true },
                { label: "No", value: false },
              ],
              rules: [{ required: true, message: "Please select an option!" }],
            },
            {
              label: "Status",
              name: "status",
              type: "select",
              options: [
                { label: "Active", value: "ACTIVE" },
                { label: "Inactive", value: "INACTIVE" },
                { label: "Softdelete", value: "SOFTDELETE" },
              ],
              rules: [{ required: true, message: "Please select status!" }],
            },
          ]}
          submitThunk={editUserThunk}
          payloadBuilder={(values) => {
            const payload = {
              id: editingUser.key,
              empID: values.empID,
              name: values.name,
              email: values.email,
              status: values.status,
              canDelegateAccess: values.canDelegateAccess,
              historySource: "backend",
              historyAction: "update",
              historyUserID: storeLIUId,
            };
            if (values.role && values.role !== editingUser?.role) {
              payload.roleID = values.role;
            }
            return payload;
          }}
          successMessage="User updated successfully."
          currentPath={currentPath}
          refreshListThunk={getUserListThunk}
        />
      </div>
    </div>
  );
};

export default Users;
